import { useState } from "react";

const ZOOM_STEPS = [50, 75, 100, 125, 150, 200] as const;
const DEFAULT_STEP = ZOOM_STEPS.indexOf(100);
// A zoom key (Figma "control · −", "control · +"): its glyph, as the zoom
// level beside it is set.
const ZOOM_KEY =
  "cursor-pointer rounded-lg border border-ds-slate-100 bg-ds-white px-2.5 py-1 font-semibold text-ds-slate-950 disabled:cursor-not-allowed disabled:opacity-40";

/**
 * An image too wide for its frame, read by zooming and scrolling sideways
 * (Figma "D3.explore.article.table-open": a table beside the text). The frame
 * scrolls; the zoom scales the image against the frame's width.
 *
 * A UI primitive, so it owns its zoom: which step is shown is machinery. The
 * caller keys it on the image, so the next image opens at 100%.
 */
export function ZoomableImage({
  src,
  alt,
  hint,
  zoomOutLabel,
  zoomInLabel,
}: {
  src: string;
  alt: string;
  // Shown beside the controls, e.g. "Wide table: scroll sideways or zoom out".
  hint: string;
  zoomOutLabel: string;
  zoomInLabel: string;
}): React.JSX.Element {
  const [step, setStep] = useState<number>(DEFAULT_STEP);
  const zoom = ZOOM_STEPS[step];
  if (zoom === undefined) {
    throw new Error(`should not happen: zoom step ${String(step)}`);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-auto rounded-lg border border-ds-slate-100 bg-ds-white">
        <img
          src={src}
          alt={alt}
          className="block max-w-none"
          style={{ width: `${String(zoom)}%` }}
        />
      </div>
      {/* Figma "table controls": three text keys and the hint, 13/18. */}
      <div className="flex items-center gap-2 text-desktop-meta text-ds-slate-500">
        <button
          type="button"
          aria-label={zoomOutLabel}
          disabled={step === 0}
          onClick={() => {
            setStep(step - 1);
          }}
          className={ZOOM_KEY}
        >
          −
        </button>
        <span className="rounded-lg border border-ds-slate-100 bg-ds-white px-2.5 py-1 font-semibold text-ds-slate-950">
          {`${String(zoom)} %`}
        </span>
        <button
          type="button"
          aria-label={zoomInLabel}
          disabled={step === ZOOM_STEPS.length - 1}
          onClick={() => {
            setStep(step + 1);
          }}
          className={ZOOM_KEY}
        >
          +
        </button>
        <span>{hint}</span>
      </div>
    </div>
  );
}
